import { useState, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import cn from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { Popover } from 'react-tiny-popover';
import { ReactionSelector, useMessageContext } from 'stream-chat-react';
import { customReactions } from '../../utils/reactions';
import Emoji from '../../icons/Emoji';
import ReactionsPopoverBg from '../../icons/ReactionsPopoverBg';

interface ReactionsPopoverProps {
    onReactionClick?: (reactionType, event) => Promise<void>;
    onTogglePopover?: (value: boolean) => void;
}

const ReactionsPopover: React.FC<ReactionsPopoverProps> = ({
    onReactionClick,
    onTogglePopover,
}) => {
    const classes = useStyles();

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const { reactionSelectorRef } = useMessageContext();

    const closePopover = useCallback(() => {
        if (!isPopoverOpen) {
            return;
        }
        setIsPopoverOpen(false);
        onTogglePopover?.(false);
    }, [isPopoverOpen]);

    const togglePopover = () => {
        setIsPopoverOpen((isPopoverOpen) => {
            onTogglePopover?.(!isPopoverOpen);
            return !isPopoverOpen;
        });
       
    };

    useEffect(() => {
        const throttledClosePopover = throttle(closePopover, 300);
        window.addEventListener('scroll', throttledClosePopover, true);
        return () => {
            window.removeEventListener('scroll', throttledClosePopover);
        };
    }, [closePopover]);

    return (
        <Popover
            isOpen={isPopoverOpen}
            positions={['top', 'right']}
            onClickOutside={closePopover}
            containerClassName={cn(classes.popoverContainer, 'global-reaction-popover-container')}
            content={() => (
                <div onClick={togglePopover}>
                    <ReactionsPopoverBg />
                    <ReactionSelector
                        handleReaction={onReactionClick}
                        additionalEmojiProps={{ size: 16 }}
                        detailedView={false}
                        reactionOptions={customReactions}
                        ref={reactionSelectorRef}
                    />
                </div>
            )}
        >
            <button
                className={cn(classes.emojiBtn, 'reaction-emoji-button')}
                onClick={togglePopover}
                data-testid="reactionPopoverBtn"
            >
                <Emoji />
            </button>
        </Popover>
    );
};

const useStyles = makeStyles(
    theme => ({
        popoverContainer: {
            top: `6px !important`,
            zIndex: 1001,
            '& .str-chat__reaction-selector': {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 32,
                backgroundImage: 'none',
                boxShadow: 'none',
                background: 'none',
                border: 'none',
            },
            '& ul': {
                margin: `0 8px`,
            },
        },
        emojiBtn: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '2.4rem',
            height: '2.4rem',
            borderRadius: '50%',
            backgroundColor: theme.palette.neutral[100],
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s linear',
            '&:hover': {
                backgroundColor: theme.palette.greys[300],
            },
            '&:active': {
                backgroundColor: theme.palette.neutral[500],
            },
        },

        // WORKAROUND
        // For unknown reason jss got mass and create wrong jss for popoverContainer class
        // this should provider another global override for the reaction pop over
        '@global': {
            '.global-reaction-popover-container': {
                top: `6px !important`,
                zIndex: 1001,
                '& .str-chat__reaction-selector': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 32,
                    backgroundImage: 'none',
                    boxShadow: 'none',
                    background: 'none',
                    border: 'none',
                },
                '& ul': {
                    margin: `0 8px`,
                },
            },
            '.reaction-emoji-button': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '2.4rem',
                height: '2.4rem',
                borderRadius: '50%',
                backgroundColor: theme.palette.greys[200],
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s linear',
                '&:hover': {
                    backgroundColor: theme.palette.greys[300],
                },
                '&:active': {
                    backgroundColor: theme.palette.neutral[500],
                },
            },
        },
        // END WORKAROUND
    }),
    {
        classNamePrefix: 'ReactionsPopover',
    }
);

export default ReactionsPopover;
